import { useCallback } from 'react';
import { useToast } from 'hooks/useToast';

import * as customerApi from 'api/customerApi';
import { useAppSelector, useAppDispatch } from 'store/hooks/useStore';
import {
  createCustomerForForms,
  CustomerForForms,
  createCustomerForFormsWithTargets,
} from 'types/customer';

import {
  customersActions,
  customersSelectors,
} from 'store/reducers/releaseReducers/releaseTaskReducer/customersSlice';
import { Update } from '@reduxjs/toolkit';
import { editSelectors } from 'store/reducers/releaseReducers/releaseTaskReducer/editSlice';

/**
 * 顧客一覧 store の実装をコンポーネントから隠すための中間層
 * @returns 顧客一覧 store の selector のラッパーと action dispatch 処理 のラッパー
 */
export const useCustomerStore = () => {
  const dispatch = useAppDispatch();
  const toast = useToast();

  const loading = useAppSelector(customersSelectors.loading);
  const id = useAppSelector(customersSelectors.id);
  const name = useAppSelector(customersSelectors.name);
  const detail = useAppSelector(customersSelectors.detail);
  const total = useAppSelector(customersSelectors.total);
  const filteredCustomers = useAppSelector(
    customersSelectors.filteredCustomers,
  );
  const selectedCustomers = useAppSelector(
    customersSelectors.selectedCustomers,
  );
  const selectedCustomersLength = useAppSelector(
    customersSelectors.selectedCustomersLength,
  );
  const isSelectedAll = useAppSelector(customersSelectors.isSelectedAll);
  const isIndeterminate = useAppSelector(customersSelectors.isIndeterminate);

  // 顧客一覧をロードした際、対象顧客にチェックを入れるために利用する
  const selectedReleaseTask = useAppSelector(editSelectors.selectedReleaseTask);

  const init = useCallback(() => {
    dispatch(customersActions.init());
  }, [dispatch]);

  const fetchCustomers = useCallback(async () => {
    dispatch(customersActions.loadStarted());
    const result = await customerApi.fetchCustomers();
    if (result.ok) {
      const customers = createCustomerForForms(result.value);
      dispatch(customersActions.fetchCustomersSuccess(customers));
    } else {
      toast({
        status: 'error',
        description: `顧客一覧の取得に失敗しました: ${result.err.message}`,
      });
    }
    dispatch(customersActions.loadEnded());
  }, [dispatch, toast]);

  const fetchCustomersAndSelectTargets = useCallback(async () => {
    if (!selectedReleaseTask) {
      return;
    }
    const targets: number[] = selectedReleaseTask?.targets || [];
    dispatch(customersActions.loadStarted());
    const result = await customerApi.fetchCustomers();
    if (result.ok) {
      // targets に含まれる顧客が選択された状態になった顧客一覧を生成する
      const customersWithTargets = createCustomerForFormsWithTargets(
        result.value,
        targets,
      );
      dispatch(customersActions.fetchCustomersSuccess(customersWithTargets));
    } else {
      toast({
        status: 'error',
        description: `顧客一覧の取得に失敗しました: ${result.err.message}`,
      });
    }
    dispatch(customersActions.loadEnded());
  }, [dispatch, selectedReleaseTask, toast]);

  const toggleCustomer = useCallback(
    (customerId: number, nextCheckState: boolean) => {
      dispatch(
        customersActions.customerUpdated({
          id: customerId,
          changes: { isSelected: nextCheckState },
        }),
      );
    },
    [dispatch],
  );

  const toggleAllCustomers = useCallback(() => {
    const nextCheckState = !isSelectedAll;
    const updates: Update<CustomerForForms>[] = filteredCustomers.map(
      (customer) => ({
        id: customer.id,
        changes: {
          isSelected: nextCheckState,
        },
      }),
    );
    dispatch(customersActions.customersUpdated(updates));
  }, [dispatch, filteredCustomers, isSelectedAll]);

  /**
   * 配信対象の顧客を選択している状態を解除します。
   */
  const uncheckAllCustomers = useCallback(() => {
    dispatch(customersActions.uncheckAllCustomers());
  }, [dispatch]);

  const changeId = useCallback(
    (customerId: string) => {
      dispatch(
        customersActions.idChanged({
          id: customerId,
        }),
      );
    },
    [dispatch],
  );

  const changeName = useCallback(
    (_name: string) => {
      dispatch(
        customersActions.nameChanged({
          name: _name,
        }),
      );
    },
    [dispatch],
  );

  const changeDetail = useCallback(
    (_detail: string) => {
      dispatch(
        customersActions.detailChanged({
          detail: _detail,
        }),
      );
    },
    [dispatch],
  );

  return {
    loading,
    id,
    name,
    detail,
    total,
    filteredCustomers,
    selectedCustomers,
    selectedCustomersLength,
    isSelectedAll,
    isIndeterminate,
    init,
    fetchCustomers,
    fetchCustomersAndSelectTargets,
    toggleCustomer,
    toggleAllCustomers,
    uncheckAllCustomers,
    changeId,
    changeName,
    changeDetail,
  };
};
